import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { StyledTableCell, StyledTableRow } from "./Styles/TableAndCellStyles";

const MATCH_BASIS_LABEL = {
  phone_last9: "Phone (last 9 digits)",
};

const firstSuggestedLandlord = (row) => {
  const list = row?.suggested_landlords;
  if (!Array.isArray(list) || list.length === 0) return null;
  return list[0];
};

export const getPhoneMatchRegisteredLandlordId = (row) => {
  if (row?.matched_landlord_id != null) return row.matched_landlord_id;
  const suggested = firstSuggestedLandlord(row);
  if (suggested?.landlord_id != null) return suggested.landlord_id;
  return row?.landlord_id ?? row?.registered_landlord_id ?? row?.related_landlord_id ?? null;
};

export const getPhoneMatchRegisteredLandlordLabel = (row) => {
  if (row?.matched_landlord_name) return row.matched_landlord_name;
  const suggested = firstSuggestedLandlord(row);
  if (suggested) return suggested.business_name ?? suggested.landlord_name ?? null;
  return row?.landlord_name ?? row?.business_name ?? null;
};

/** Stable row key for selection (one row per occupancy × suggested registered landlord). */
export const getPhoneMatchRowKey = (row) => {
  const occ = row?.occupancy_id;
  const landlord = getPhoneMatchRegisteredLandlordId(row);
  if (occ != null && landlord != null) return `${occ}-${landlord}`;
  if (occ != null) return String(occ);
  return JSON.stringify(row ?? {});
};

export default function AnonymousLandlordPhoneMatchTable({
  data = [],
  loading,
  error,
  selectedRowKeys = [],
  onToggleRow,
  onToggleAll,
  onRefresh,
  isRefreshing,
}) {
  const rowKeys = data.map((row) => getPhoneMatchRowKey(row));
  const selectableRowKeys = data
    .filter((row) => getPhoneMatchRegisteredLandlordId(row) != null)
    .map((row) => getPhoneMatchRowKey(row));
  const selectedOnPageCount = selectableRowKeys.filter((key) => selectedRowKeys.includes(key)).length;
  const allSelected = selectableRowKeys.length > 0 && selectedOnPageCount === selectableRowKeys.length;
  const someSelected = selectedOnPageCount > 0 && !allSelected;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {typeof error === "string" ? error : "Failed to load phone match candidates."}
      </Alert>
    );
  }

  return (
    <Paper>
      <Box display="flex" justifyContent="flex-end" p={1}>
        <Button size="small" variant="outlined" onClick={onRefresh} disabled={isRefreshing}>
          {isRefreshing ? "Refreshing…" : "Refresh list"}
        </Button>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={onToggleAll}
                  disabled={selectableRowKeys.length === 0}
                />
              </StyledTableCell>
              <StyledTableCell>Matched landlord</StyledTableCell>
              <StyledTableCell>Occupancy</StyledTableCell>
              <StyledTableCell>Tenant</StyledTableCell>
              <StyledTableCell>Property / Unit</StyledTableCell>
              <StyledTableCell>Placeholder landlord</StyledTableCell>
              <StyledTableCell>Detail mobile</StyledTableCell>
              <StyledTableCell>Detail account</StyledTableCell>
              <StyledTableCell>Match</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell colSpan={9} align="center">
                  No phone-match candidates right now. Either nothing is in scope, or there is no overlap between
                  WhatsApp-onboard landlord details and registered landlord accounts.
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              data.map((row) => {
                const rowKey = getPhoneMatchRowKey(row);
                const checked = selectedRowKeys.includes(rowKey);
                const registeredId = getPhoneMatchRegisteredLandlordId(row);
                const registeredLabel = getPhoneMatchRegisteredLandlordLabel(row);
                const canAttach = registeredId != null;
                const tenantLabel = [row.tenant_number, row.tenant_id != null ? `#${row.tenant_id}` : ""]
                  .filter(Boolean)
                  .join(" · ");
                const matchLabel = MATCH_BASIS_LABEL[row.match_basis] || row.match_basis || "—";

                return (
                  <StyledTableRow key={rowKey} hover>
                    <StyledTableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={checked}
                        disabled={!canAttach}
                        onChange={() => onToggleRow(row)}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {canAttach ? registeredLabel || "—" : "No suggested registered landlord"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {canAttach ? `ID ${registeredId}` : "Add a match on the server or assign manually"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">{row.occupancy_reference || `—`}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID {row.occupancy_id ?? "—"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>{tenantLabel || "—"}</StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">{row.property_name || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.unit_name || "—"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">{row.placeholder_landlord_name || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID {row.placeholder_landlord_id ?? "—"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>{row.detail_mobile_number || "—"}</StyledTableCell>
                    <StyledTableCell>{row.detail_account_number || "—"}</StyledTableCell>
                    <StyledTableCell>
                      <Chip size="small" label={matchLabel} variant="outlined" />
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
